import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, TriangleAlert, Eye, Info, Database, FileText, Link as LinkIcon, Gavel } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const sanitizeAndFormatHtml = (content) => {
    if (!content || typeof content !== 'string') return '';
    let html = content;
    html = html.replace(/<a\s+[^>]*?>/gi, (tag) => {
        const attrMatch = tag.match(/(?:["']?(?:link|href)\s*=\s*\\?["']?\s*)([^"'\\>]+?)(?:\s*\\?["']?\s*)(?:>|\s|$)/i);
        if (!attrMatch) return tag;
        const rawUrl = attrMatch[1].trim().replace(/^["']|["']$/g, '');
        const targetMatch = tag.match(/target\s*=\s*["']?([^"'>\s]+)["']?/i);
        const targetAttr = targetMatch ? ` target="${targetMatch[1]}"` : '';
        return `<a href="${rawUrl}"${targetAttr}>`;
    });
    if (!/<(?:br|p|div)\b/i.test(html)) {
        html = html.replace(/\r?\n/g, '<br />');
    }
    return html;
};

const SecuredAssetsDefaultPage = () => {
    const [loading, setLoading] = useState(true);

    // Default Settings State
    const [defaultSettings, setDefaultSettings] = useState({
        section_tag: 'Transparency & Clarity',
        section_title: 'What Happens in the Event of Default',
        section_intro: '',
        commitment_text_1: '',
        commitment_text_2: '',
        auction_notice_text: 'Interested buyers can review the <a href="https://ahamhfc.com/aham-housing-finance-e-auction-sale-notice">current e-auction sale notices</a> for eligible secured properties offered by Aham Housing Finance.'
    });

    // Link inserter state
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');

    const handleInsertLink = () => {
        if (!linkUrl.trim()) {
            toast.error('Please enter a valid URL (e.g. https://ahamhfc.com/aham-housing-finance-e-auction-sale-notice)');
            return;
        }
        const label = linkText.trim() || 'current e-auction sale notices';
        const cleanUrl = linkUrl.trim();
        const tag = `<a href="${cleanUrl}">${label}</a>`;
        const currentValue = defaultSettings.auction_notice_text || '';
        const updatedValue = currentValue ? `${currentValue} ${tag}` : tag;
        setDefaultSettings(prev => ({ ...prev, auction_notice_text: updatedValue }));
        toast.success(`Inserted link: ${label}`);
        setShowLinkModal(false);
        setLinkText('');
        setLinkUrl('');
    };

    // Default Steps State
    const [defaultSteps, setDefaultSteps] = useState([]);
    const [isStepModalOpen, setIsStepModalOpen] = useState(false);
    const [stepModalData, setStepModalData] = useState({
        step_number: '01',
        title: '',
        description: '',
        icon_name: 'Bell',
        display_order: 0,
        is_active: true
    });
    const [editingStep, setEditingStep] = useState(null);
    const [stepDeleteConfirm, setStepDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, stepsRes] = await Promise.all([
                axios.get('/api/admin/secured-assets/default-settings'),
                axios.get('/api/admin/secured-assets/default-steps')
            ]);

            if (settingsRes.data.success && settingsRes.data.data) {
                setDefaultSettings(settingsRes.data.data);
            }
            if (stepsRes.data.success) {
                setDefaultSteps(stepsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setDefaultSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/secured-assets/default-settings', defaultSettings);
            toast.success('Settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleAddStep = () => {
        setEditingStep(null);
        setStepModalData({
            step_number: `0${defaultSteps.length + 1}`,
            title: '',
            description: '',
            icon_name: 'Bell',
            display_order: (defaultSteps.length + 1),
            is_active: true
        });
        setIsStepModalOpen(true);
    };

    const handleEditStep = (step) => {
        setEditingStep(step);
        setStepModalData({
            ...step,
            is_active: Boolean(step.is_active)
        });
        setIsStepModalOpen(true);
    };

    const handleStepSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingStep) {
                await axios.put(`/api/admin/secured-assets/default-steps/${editingStep.id}`, stepModalData);
                toast.success('Step updated');
            } else {
                await axios.post('/api/admin/secured-assets/default-steps', stepModalData);
                toast.success('Step created');
            }
            setIsStepModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error('Failed to save step');
        }
    };

    const confirmDeleteStep = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets/default-steps/${stepDeleteConfirm.id}`);
            toast.success('Step deleted');
            setStepDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting step:', error);
            toast.error('Failed to delete step');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto pb-12">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Secured Assets - Transparency & Clarity</h1>
                    <p className="text-slate-500 mt-1">Manage default process steps and commitment information</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex items-center gap-2 mb-6 text-primary-900 border-b border-slate-50 pb-4">
                    <Info size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Main Section Settings</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                        <input
                            type="text"
                            name="section_tag"
                            value={defaultSettings.section_tag || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={defaultSettings.section_title || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Intro Text</label>
                        <textarea
                            name="section_intro"
                            value={defaultSettings.section_intro || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                        />
                    </div>
                </div>

                {/* Auction Notice with Inter-Link Helper */}
                <div className="border-t border-slate-100 pt-6 mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700">Auction Notice Paragraph (Middle Section)</label>
                            <p className="text-xs text-slate-500">Appears between the 5 process step cards and the commitment note box.</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowLinkModal(!showLinkModal);
                                setLinkText('');
                                setLinkUrl('');
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors border border-indigo-200"
                        >
                            <LinkIcon size={14} />
                            <span>{showLinkModal ? 'Cancel Link' : '+ Insert Link'}</span>
                        </button>
                    </div>

                    {showLinkModal && (
                        <div className="bg-indigo-50/80 border border-indigo-200 rounded-xl p-3 mb-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-indigo-900">Insert Link into Auction Notice</span>
                                <span className="text-[11px] text-indigo-600">e.g. /contact, /branches, or https://...</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Link Text</label>
                                    <input
                                        type="text"
                                        value={linkText}
                                        onChange={e => setLinkText(e.target.value)}
                                        placeholder="e.g. current e-auction sale notices"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Target URL</label>
                                    <input
                                        type="text"
                                        value={linkUrl}
                                        onChange={e => setLinkUrl(e.target.value)}
                                        placeholder="e.g. https://ahamhfc.com/aham-housing-finance-e-auction-sale-notice"
                                        className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setShowLinkModal(false)}
                                    className="px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleInsertLink}
                                    className="px-3 py-1 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm"
                                >
                                    Insert &lt;a&gt; Tag
                                </button>
                            </div>
                        </div>
                    )}

                    <textarea
                        name="auction_notice_text"
                        value={defaultSettings.auction_notice_text || ''}
                        onChange={handleSettingsChange}
                        rows={3}
                        placeholder="Interested buyers can review the <a href=&quot;https://ahamhfc.com/aham-housing-finance-e-auction-sale-notice&quot;>current e-auction sale notices</a> for eligible secured properties offered by Aham Housing Finance."
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all resize-y text-sm font-normal"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                        Tip: HTML anchor tags and line breaks are fully supported.
                    </p>

                    {/* Live Preview of Notice with Anchor Links */}
                    {defaultSettings.auction_notice_text && (
                        <div className="mt-3 bg-[#283079] p-5 rounded-2xl border border-indigo-900/30 text-white space-y-3 shadow-md">
                            <div className="flex items-center justify-between border-b border-white/15 pb-2.5">
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                    <span className="text-xs font-bold uppercase tracking-wider text-yellow-400">NOTICE LIVE PREVIEW</span>
                                </div>
                                <span className="text-xs text-gray-300">Live preview with clickable links</span>
                            </div>
                            <div className="flex items-center gap-4 pt-1">
                                <Gavel className="w-9 h-9 text-yellow-400 flex-shrink-0" />
                                <div
                                    className="text-gray-200 text-sm leading-relaxed [&_a]:text-yellow-400 [&_a]:underline hover:[&_a]:text-yellow-300 [&_a]:font-semibold [&_a]:transition-colors"
                                    dangerouslySetInnerHTML={{ __html: sanitizeAndFormatHtml(defaultSettings.auction_notice_text) }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 mb-6 text-primary-900 border-b border-slate-50 pb-4 pt-4">
                    <Eye size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Commitment Note (Bottom Card)</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Proceeds Sentence (Commitment 1)</label>
                        <textarea
                            name="commitment_text_1"
                            value={defaultSettings.commitment_text_1 || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-20 resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Final Commitment (Commitment 2 - Bold)</label>
                        <input
                            type="text"
                            name="commitment_text_2"
                            value={defaultSettings.commitment_text_2 || ''}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Process Steps */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex justify-between items-center mb-6 border-b border-slate-50 pb-4">
                    <div className="flex items-center gap-2 text-primary-900">
                        <TriangleAlert size={20} className="font-bold" />
                        <h2 className="text-lg font-bold">Default Process Steps</h2>
                    </div>
                    <button
                        onClick={handleAddStep}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Step
                    </button>
                </div>

                <div className="space-y-4">
                    {defaultSteps.map((step) => (
                        <div key={step.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-start gap-4 group">
                            <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-primary-600 font-bold text-lg flex-shrink-0 shadow-sm">
                                {step.step_number}
                            </div>
                            <div className="flex-grow">
                                <h3 className="text-slate-800 font-bold">{step.title}</h3>
                                <p className="text-slate-600 text-sm mt-1">{step.description}</p>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className="text-xs text-slate-400">Icon: {step.icon_name}</span>
                                    <span className="text-xs text-slate-400">Order: {step.display_order}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${step.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-200 text-slate-500'}`}>
                                        {step.is_active ? 'Active' : 'Hidden'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleEditStep(step)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil size={18} /></button>
                                <button onClick={() => setStepDeleteConfirm({ isOpen: true, id: step.id })} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={18} /></button>
                            </div>
                        </div>
                    ))}
                    {defaultSteps.length === 0 && (
                        <div className="text-center py-8 text-slate-400">No steps found. Add your first step!</div>
                    )}
                </div>
            </div>

            {/* Step Modal */}
            {isStepModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingStep ? 'Edit Process Step' : 'Add New Step'}</h2>
                            <button onClick={() => setIsStepModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleStepSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Step Number</label>
                                    <input
                                        type="text"
                                        value={stepModalData.step_number}
                                        onChange={(e) => setStepModalData({ ...stepModalData, step_number: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="01, 02..."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Icon Name</label>
                                    <input
                                        type="text"
                                        value={stepModalData.icon_name}
                                        onChange={(e) => setStepModalData({ ...stepModalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="Bell, Landmark..."
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={stepModalData.title}
                                    onChange={(e) => setStepModalData({ ...stepModalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={stepModalData.description}
                                    onChange={(e) => setStepModalData({ ...stepModalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={stepModalData.display_order}
                                        onChange={(e) => setStepModalData({ ...stepModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={stepModalData.is_active}
                                            onChange={(e) => setStepModalData({ ...stepModalData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{stepModalData.is_active ? 'Active' : 'Hidden'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button type="button" onClick={() => setIsStepModalOpen(false)} className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all">Cancel</button>
                                <button type="submit" className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg">{editingStep ? 'Update Step' : 'Create Step'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={stepDeleteConfirm.isOpen}
                onClose={() => setStepDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteStep}
                title="Delete Step"
                message="Are you sure you want to delete this process step? This action cannot be undone."
            />
        </div>
    );
};

export default SecuredAssetsDefaultPage;
